/*
  <collapsible-component
    data-options='
      {
        "closeSiblings": true
      }
    '
  >
    <div class="group" data-collapsible-group>
      <div class="collapsible__group group" data-collapsible-group {{ block.shopify_attributes }}>
        <button
          type="button"
          class="collapsible__trigger {{ section.settings.button_color_scheme }} flex w-full items-center justify-between {% if section.settings.button_color_scheme == '' -%}py-4 pe-4 no-underline transition group-[.collapsible-is-open]:font-heading{%- endif -%}"
          data-collapsible-trigger>
          Trigger text
          <span class="collapsible__trigger-icons">
            {%- render 'icons', icon: 'plus', icon_class: 'w-3 block group-[.collapsible-is-open]:hidden' -%}
            {%- render 'icons', icon: 'minus', icon_class: 'w-3 hidden group-[.collapsible-is-open]:block' -%}
          </span>
        </button>
        <div
          class="collapsible__target"
          data-collapsible-target>
          <div class="rte overflow-hidden">
            <div class="pb-4 pt-2">
              {{ block.settings.content }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </collapsible-component>
*/

if (!customElements.get('collapsible-component')) {
  class Collapsible extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        classToToggle: 'collapsible-is-open',
        closeSiblings: false,
        closeChildren: true,
        onHover: false,
        hoverDelay: 0,
        closeOnMouseleave: false,
        isMobileMenu: false,
        isDesktopMenu: false,
        breakpointMax: false,
        trapFocus: true,
        openOnMobile: false,
      };

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      this.wrapper = this.querySelectorAll('[data-collapsible-wrapper]');

      // toggle the header class
      if (this.options.isDesktopMenu) {
        this.header = document.querySelector('header-component');
      }

      this.touch = document.documentElement.classList.contains('touch');

      this.timeout = null;

      this.init();

      window.addEventListener('resize', debounce(() => {
        this.init();
      }, 50));
    }

    init() {
      // Check if the window width allows for the collapsibles to be rendered
      let init;
      if (this.options.breakpointMax === false) {
        init = true;
      }
      else {
        if (this.options.breakpointMax < windowWidth()) {
          init = false;
        }
        else {
          init = true;
        }
      }
      this.construct(init);
    }

    construct(init = true) {

      // Init elements
      this.groups = this.hasAttribute('data-collapsible-group')
        ? [this.closest('[data-collapsible-group]')]
        : this.querySelectorAll('[data-collapsible-group]');
      this.triggers = this.querySelectorAll('[data-collapsible-trigger]');

      // Remove all event listeners
      this.triggers.forEach(trigger => {
        trigger.removeEventListener('click', this.debounceClickEvent);
        if (this.options.onHover) {
          trigger.removeEventListener('mouseenter', this.debouncedOnMouse);
        }
      });
      this.groups.forEach(group => {
        if (this.options.onHover) group.removeEventListener('mouseleave', this.debouncedOnMouse);
      });

      if (this.options.closeOnMouseleave) this.removeEventListener('mouseleave', this.debouncedOnMouse);

      // the collapsible breakpoint has been reached, so the collapsibles should be opened
      if (!init) {
        this.groups.forEach(group => this.open(group));

        // hide all trigger icons
        this.triggers.forEach(trigger => {
          if (this.options.breakpointMax !== false) trigger.querySelector('.icon').classList.add('hidden');
        });

        return;
      }
      else if (init) {
        // open on mobile, close on desktop
        if (this.options.openOnMobile) {
          if (windowWidth() < 768) {
            this.openAll();
            this.options.closeSiblings = false;
          }
          else {
            this.closeAll();
            this.options.closeSiblings = true;
          }
        }
        // close the collapsibles because the breakpoint demands them to be collapsed
        if (this.options.breakpointMax !== false) {
          this.groups.forEach(group => {
            if (group.classList.contains(this.options.classToToggle)) {
              this.close(group, false);
            }
          });
        }
      }

      // Trigger events on [data-collapsible-trigger]
      this.triggers.forEach(trigger => {

        // show all icons
        if (this.options.breakpointMax !== false) trigger.querySelector('.icon').classList.remove('hidden');

        // add event listeners
        trigger.addEventListener('click', this.debounceClickEvent.bind(this));
        if (this.options.onHover) {
          trigger.addEventListener('mouseenter', this.debouncedOnMouse.bind(this, 'open'));
        }
      });

      // Trigger events on [data-collapsible-group]
      this.groups.forEach(group => {
        if (this.options.onHover) {
          group.addEventListener('mouseleave', this.debouncedOnMouse.bind(this, 'close'));
        }
      });

      // Trigger mouseleave on container <collapsible-component>
      if (this.options.closeOnMouseleave) {
        this.addEventListener('mouseleave', this.debouncedOnMouse.bind(this, 'closeAll'));
      }
    }

    /*
      Debounce click event
      @param event {object}: mouse event
      Called by add event listener -> reducer.
    */
    debounceClickEvent(event) {
      if (!event) return false;

      const trigger = event.target.closest('[data-collapsible-trigger]');
      if (!trigger) return;

      const group = event.target.closest('[data-collapsible-group]');
      if (!group) return;

      // Always prevent default when clicking the trigger so links don't scroll/navigate on open/close
      event.preventDefault();
      event.stopImmediatePropagation();

      this.toggle(group);
    }

    /*
      Debounced mouse event
      @param event {object}: mouse event
      @param type {string}: open/close/closeAll
      Called by add event listener -> reducer.
    */
    debouncedOnMouse(type, event) {
      if (!event || this.touch) return false;

      // clear old trigger
      clearTimeout(this.timeout);

      const group = event?.target?.closest('[data-collapsible-group]');

      // add new trigger
      this.timeout = setTimeout(() => {
        switch (type) {
        case 'open':
          this.open(group);
          break;
        case 'close':
          this.close(group);
          break;
        case 'closeAll':
          this.closeAll();
          break;
        default:
          break;
        }
      }, this.options.hoverDelay);
    }

    /*
      Open collapsible group
      @param group {node}: group selector
    */
    open(group) {
      if (!group) return false;

      // Close siblings if option is on
      if (this.options.closeSiblings) {
        if (!this.options.breakpointMax || (this.options.breakpointMax !== false && windowWidth() < this.options.breakpointMax)) {
          this.closeSiblings(group);
        }
      }

      // Open active group
      group.classList.add(this.options.classToToggle);

      // Only use focus when item is not hovered
      if (!this.options.onHover) {
        if(this.options.trapFocus) {
          trapFocus(group);
        }
      }

      if (this.options.isDesktopMenu) this.header.toggleHeaderClass(true);
    }

    /*
      close collapsible group,
      @param group {node}: group selector
    */
    close(group, focus = true) {
      if (!group) return false;

      if (focus) {
        removeTrapFocus(group);

        // Set the focus to the trigger when closing a collapsible
        if (!this.options.onHover) group.querySelector('[data-collapsible-trigger]').focus();
      }

      // Close active group
      group.classList.remove(this.options.classToToggle);
    }

    /*
      Toggle controller, open / close collapsibles
      @param group {node}: group selector
    */
    toggle(group) {
      if (!group) return false;

      // Check if already open
      if (!group.classList.contains(this.options.classToToggle)) {
        this.open(group);
      }
      else {
        // Close child collapsibles
        if (this.options.closeChildren) group.querySelectorAll('[data-collapsible-group]').forEach(group => this.close(group));

        // Close this group
        this.close(group);

        if (this.options.isDesktopMenu) this.header.toggleHeaderClass(false);
      }
    }

    /*
      Close all sibling collapsibles
      @param currentGroup {node}: current group selector
    */
    closeSiblings(currentGroup = null) {
      this.groups.forEach(group => {
        if (group !== currentGroup && !group.contains(currentGroup) && !currentGroup.contains(group)) this.close(group, false);
      });
    }

    /*
      Open+ all collapsibles
    */
    openAll() {
      this.groups.forEach(group => group.classList.add(this.options.classToToggle));
    }

    /*
      Close all collapsibles
    */
    closeAll() {
      this.groups.forEach(group => group.classList.remove(this.options.classToToggle));

      // Close desktop
      if (this.options.isDesktopMenu) this.header.toggleHeaderClass(false);
    }
  }

  window.Collapsible = Collapsible;

  customElements.define('collapsible-component', Collapsible);
}
